<!-- This example requires Tailwind CSS v2.0+ -->
<template>
  <div class="min-h-screen flex flex-col">
    <div class="top-bar">
      <NotOnBoarded v-if="notOnboarded">
        <p class="text-sm text-yellow-700">
          Before using this portal, your <strong>local office</strong>,
          <strong>contact</strong> and <strong>address</strong> information must
          be up to date.
          <router-link
            :to="{ name: 'claimant-info' }"
            class="font-medium underline text-yellow-700 hover:text-yellow-600"
          >
            Manage your account
          </router-link>
        </p>
      </NotOnBoarded>
    </div>
    <Disclosure as="nav" class="bg-white shadow-sm" v-slot="{ open }">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="flex justify-between h-fit">
          <div class="flex">
            <div class="flex-shrink-0 flex items-center">
              <div class="logo--area block lg:hidden w-64 h-auto">
                <router-link :to="{ name: 'home' }">
                  <!-- <DefaultLogo /> -->
                  <LogoNoText :height="80" :width="80" />
                </router-link>
              </div>
              <div class="logo--area hidden lg:block w-64 h-auto">
                <router-link :to="{ name: 'home' }">
                  <!-- <DefaultLogo /> -->
                  <LogoNoText :height="100" :width="100" />
                </router-link>
              </div>
            </div>
            <div
              data-tour="main-nav"
              class="hidden tracking-wider sm:-my-px sm:ml-6 sm:flex sm:space-x-8"
            >
              <router-link
                v-for="item in navigation"
                :key="item.name"
                :to="{ name: item.route }"
                :data-tour="item.route === 'claims-index' ? 'nav-claims' : undefined"
                class="border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium"
                :aria-current="item.current ? 'page' : undefined"
                >{{ item.name }}</router-link
              >
            </div>
          </div>

          <div class="hidden sm:ml-6 sm:flex sm:items-center z-10">
            <button
              v-if="showTourButton"
              type="button"
              data-tour="help-button"
              class="mr-2 p-1 rounded-full text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              aria-label="Take a tour of this page"
              @click="startTour"
            >
              <QuestionMarkCircleIcon class="h-6 w-6" aria-hidden="true" />
            </button>
            <!-- Profile dropdown -->
            <Menu as="div" class="ml-3 relative" data-tour="user-menu">
              <div>
                <MenuButton
                  class="bg-white flex text-sm rounded-full focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 items-center gap-x-2"
                >
                  <span class="sr-only">Open user menu</span>
                  <span class="capitalize"
                    >{{ authUser.firstName }} {{ authUser.lastName }}</span
                  >
                  <UserCircleIcon class="h-8 w-8 rounded-full" />
                </MenuButton>
              </div>
              <transition
                enter-active-class="transition ease-out duration-200"
                enter-from-class="transform opacity-0 scale-95"
                enter-to-class="transform opacity-100 scale-100"
                leave-active-class="transition ease-in duration-75"
                leave-from-class="transform opacity-100 scale-100"
                leave-to-class="transform opacity-0 scale-95"
              >
                <MenuItems
                  class="origin-top-right absolute right-0 mt-2 w-48 rounded-md shadow-lg py-1 bg-white ring-1 ring-black ring-opacity-5 focus:outline-none"
                >
                  <MenuItem
                    v-for="item in userNavigation"
                    :key="item.name"
                    v-slot="{ active }"
                  >
                    <router-link
                      :to="{ name: item.route }"
                      :class="[
                        active ? 'bg-gray-100' : '',
                        'block px-4 py-2 text-sm text-gray-700',
                      ]"
                      >{{ item.name }}</router-link
                    >
                  </MenuItem>
                  <MenuItem>
                    <a
                      @click="logoutUser"
                      class="hover:bg-gray-100 block px-4 py-2 text-sm text-gray-700 cursor-pointer"
                      >Log Out</a
                    >
                  </MenuItem>
                </MenuItems>
              </transition>
            </Menu>
          </div>
          <div class="-mr-2 flex items-center sm:hidden">
            <!-- Mobile menu button -->
            <DisclosureButton
              class="bg-white inline-flex items-center justify-center p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              <span class="sr-only">Open main menu</span>
              <MenuIcon v-if="!open" class="block h-6 w-6" aria-hidden="true" />
              <XIcon v-else class="block h-6 w-6" aria-hidden="true" />
            </DisclosureButton>
          </div>
        </div>
      </div>

      <DisclosurePanel class="sm:hidden">
        <div class="pt-2 pb-3 space-y-1">
          <DisclosureButton
            v-for="item in navigation"
            :key="item.name"
            as="a"
            :class="[
              item.current
                ? 'bg-indigo-50 border-indigo-500 text-indigo-700'
                : 'border-transparent text-gray-600 hover:bg-gray-50 hover:border-gray-300 hover:text-gray-800',
              'block pl-3 pr-4 py-2 border-l-4 text-base font-medium',
            ]"
            :aria-current="item.current ? 'page' : undefined"
          >
            <router-link :to="{ name: item.route }">
              {{ item.name }}
            </router-link>
          </DisclosureButton>
        </div>
        <div class="pt-4 pb-3 border-t border-gray-200">
          <div class="flex items-center px-4">
            <div>
              <div class="text-base font-medium text-gray-800">
                {{ authUser.firstName }} {{ authUser.lastName }}
              </div>
              <div class="text-sm font-medium text-gray-500">
                {{ authUser.email }}
              </div>
            </div>
          </div>
          <div class="mt-3 space-y-1">
            <DisclosureButton v-for="item in userNavigation" :key="item.name">
              <router-link
                :key="item.name"
                :to="{ name: item.route }"
                class="block px-4 py-2 text-base font-medium text-gray-500 hover:text-gray-800 hover:bg-gray-100"
                >{{ item.name }}</router-link
              >
            </DisclosureButton>
            <DisclosureButton
              @click="logoutUser"
              class="block px-4 py-2 text-base font-medium text-gray-500 hover:text-gray-800 hover:bg-gray-100"
              >Log Out</DisclosureButton
            >
          </div>
        </div>
      </DisclosurePanel>
    </Disclosure>

    <div :class="[noMaxWidth ? '' : 'py-10', 'flex-1']">
      <main>
        <div
          :class="[
            noMaxWidth ? 'max-w-none' : 'max-w-7xl sm:px-6 lg:px-8',
            'mx-auto',
          ]"
        >
          <!-- Replace with your content -->
          <div :class="[noMaxWidth ? 'px-0' : 'px-4']">
            <router-view></router-view>
          </div>
          <!-- End replace -->
        </div>
      </main>
    </div>
    <footer class="bg-gray-200" aria-labelledby="footer-heading">
      <h2 id="footer-heading" class="sr-only">Footer</h2>
      <div class="mx-auto max-w-5xl px-6 pb-8 pt-12 lg:px-8">
        <div>
          <div class="outline-dashed space-y-8">
            <p class="font-bold text-sm text-gray-600 leading-6 italic">
              Reflecting on the past, building the present, securing the future!
            </p>
            <div>
              <p class="text-sm leading-6 text-gray-600">
                Monday - Friday: 9:00AM - 5:00PM
              </p>
              <p class="text-sm leading-6 text-gray-600">
                Saturday & Sunday: Closed
              </p>
            </div>
            <div class="flex space-x-2 md:order-2">
              <a
                v-for="item in socialIcons"
                :key="item.name"
                :href="item.href"
                class="text-gray-400 hover:opacity-80"
              >
                <span class="sr-only">{{ item.name }}</span>
                <component :is="item.icon" class="h-6 w-6" aria-hidden="true" />
              </a>
            </div>
          </div>
        </div>
        <div class="mt-8 border-t border-gray-300 pt-4">
          <div class="">
            <span class="text-xs md:text-sm text-gray-600">
              This application supports the following major browsers:
            </span>
            <img
              src="/img/browsers.png"
              alt="Supported browsers"
              class="w-20 inline-block"
            />
          </div>
          <p class="text-gray-600"><Copyright /></p>
        </div>
      </div>
    </footer>
    <LoadingScreen v-if="store.getters['miscData/showLoadingScreen']" />
  </div>
</template>

<script setup lang="ts">
import {
  Disclosure,
  DisclosureButton,
  DisclosurePanel,
  Menu,
  MenuButton,
  MenuItem,
  MenuItems,
} from "@headlessui/vue";
import {
  MenuIcon,
  XIcon,
  UserCircleIcon,
  QuestionMarkCircleIcon,
} from "@heroicons/vue/outline";
import { defineComponent, h } from "vue";
import { useStore } from "vuex";
import { computed } from "vue";
import { useRoute } from "vue-router";
import useUserData from "/@/composables/useUserData";
import useAuth from "/@/composables/useAuth";
import useFuneralBenefit from "/@/composables/useFuneralBenefit";
import useTour from "/@/composables/useTour";

import { useAsyncState } from "@vueuse/core";

const { logoutUser } = useAuth();
const {
  getUserBankAccounts,
  getLocalOffices,
  getPaymentTypes,
  notOnboarded,
  getAuthUser,
  getUserAddressBook,
  getUserContacts,
} = useUserData();
const { getFuneralHomes } = useFuneralBenefit();

const store = useStore();

const { startTour } = useTour();
const route = useRoute();
// Tour only covers the dashboard at launch; button hides elsewhere
// (spec: reappears with future per-page tours).
const showTourButton = computed(() => route.name === "dashboard");

const noMaxWidthRoutes = ["/", "/registration/card-renewal"]; // these routes have no max-width properties. The max-width will instead be place on the children

const noMaxWidth = computed(() =>
  noMaxWidthRoutes.includes(useRoute().fullPath)
);

const authUser: any = computed(() => store.getters["user/getAuthUser"]);

const navigation = [
  { name: "Cards", route: "card-renewal-index", current: false },
  { name: "Claims", route: "claims-index", current: false },
];

const userNavigation = [{ name: "My Account", route: "my-account" }];

const socialIcons = [
  {
    name: "Facebook",
    href: "https://www.facebook.com/thenationalinsuranceboardba/",
    icon: defineComponent({
      render: () =>
        h("svg", { fill: "#3b5998", viewBox: "0 0 24 24" }, [
          h("path", {
            "fill-rule": "evenodd",
            d: "M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z",
            "clip-rule": "evenodd",
          }),
        ]),
    }),
  },
  {
    name: "Instagram",
    href: "https://www.instagram.com/nibbahamas/",
    icon: defineComponent({
      render: () =>
        h("svg", { fill: "#d62976", viewBox: "0 0 24 24" }, [
          h("path", {
            "fill-rule": "evenodd",
            d: "M12.315 2c2.43 0 2.784.013 3.808.06 1.064.049 1.791.218 2.427.465a4.902 4.902 0 011.772 1.153 4.902 4.902 0 011.153 1.772c.247.636.416 1.363.465 2.427.048 1.067.06 1.407.06 4.123v.08c0 2.643-.012 2.987-.06 4.043-.049 1.064-.218 1.791-.465 2.427a4.902 4.902 0 01-1.153 1.772 4.902 4.902 0 01-1.772 1.153c-.636.247-1.363.416-2.427.465-1.067.048-1.407.06-4.123.06h-.08c-2.643 0-2.987-.012-4.043-.06-1.064-.049-1.791-.218-2.427-.465a4.902 4.902 0 01-1.772-1.153 4.902 4.902 0 01-1.153-1.772c-.247-.636-.416-1.363-.465-2.427-.047-1.024-.06-1.379-.06-3.808v-.63c0-2.43.013-2.784.06-3.808.049-1.064.218-1.791.465-2.427a4.902 4.902 0 011.153-1.772A4.902 4.902 0 015.45 2.525c.636-.247 1.363-.416 2.427-.465C8.901 2.013 9.256 2 11.685 2h.63zm-.081 1.802h-.468c-2.456 0-2.784.011-3.807.058-.975.045-1.504.207-1.857.344-.467.182-.8.398-1.15.748-.35.35-.566.683-.748 1.15-.137.353-.3.882-.344 1.857-.047 1.023-.058 1.351-.058 3.807v.468c0 2.456.011 2.784.058 3.807.045.975.207 1.504.344 1.857.182.466.399.8.748 1.15.35.35.683.566 1.15.748.353.137.882.3 1.857.344 1.054.048 1.37.058 4.041.058h.08c2.597 0 2.917-.01 3.96-.058.976-.045 1.505-.207 1.858-.344.466-.182.8-.398 1.15-.748.35-.35.566-.683.748-1.15.137-.353.3-.882.344-1.857.048-1.055.058-1.37.058-4.041v-.08c0-2.597-.01-2.917-.058-3.96-.045-.976-.207-1.505-.344-1.858a3.097 3.097 0 00-.748-1.15 3.098 3.098 0 00-1.15-.748c-.353-.137-.882-.3-1.857-.344-1.023-.047-1.351-.058-3.807-.058zM12 6.865a5.135 5.135 0 110 10.27 5.135 5.135 0 010-10.27zm0 1.802a3.333 3.333 0 100 6.666 3.333 3.333 0 000-6.666zm5.338-3.205a1.2 1.2 0 110 2.4 1.2 1.2 0 010-2.4z",
            "clip-rule": "evenodd",
          }),
        ]),
    }),
  },
];

useAsyncState(
  Promise.all([
    getAuthUser(),
    getUserBankAccounts(),
    getLocalOffices(),
    getPaymentTypes(),
    getFuneralHomes(),
    getUserAddressBook(),
    getUserContacts(),
  ]),
  null
);
</script>

<style lang="postcss" scoped>
.router-link-active {
  @apply border-indigo-500 text-gray-900 !important;
}
</style>
